import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';

import feather from 'feather-icons';
import { OrdersService, QueryOrderListFilterRequest } from '../../../core/services/order/order.service';
import { AuditEventFilterModel, AuditService } from '../../../core/services/audit/audit.service';
import { forkJoin, of, map, catchError, finalize, merge, Observable, tap } from 'rxjs';

type Tone = 'teal' | 'orange' | 'pink' | 'blue' | 'yellow';
type OrderType = 'customer' | 'seller';

type HubSource =
  | { kind: 'orders'; req: Partial<QueryOrderListFilterRequest> }
  | { kind: 'audit'; version?: 'v1' | 'v2'; filter: Partial<AuditEventFilterModel> };

type HubItem = {
  key: Tone;
  tone: Tone;
  index: number;
  count: number;
  title: string;
  subtitle: string;
  icon: string;
  nav?: { path: string; queryParams?: Record<string, any> };
  source?: HubSource;
};

type HubDot = { key: Tone; tone: Tone; x: number; y: number };
type HubPath = { key: Tone; tone: Tone; d: string };

@Component({
  selector: 'app-overview-hub',
  standalone: true,
  imports: [],
  templateUrl: './overview-hub.component.html',
  styleUrls: ['./overview-hub.component.scss'],
})
export class OverviewHubComponent implements AfterViewInit, OnDestroy {
  @ViewChild('stage', { read: ElementRef }) stageRef!: ElementRef<HTMLElement>;
  @ViewChild('svgRoot', { read: ElementRef }) svgRef!: ElementRef<SVGSVGElement>;
  @ViewChild('root', { read: ElementRef }) rootRef!: ElementRef<HTMLElement>;
  @ViewChild('transition', { read: ElementRef }) transitionRef!: ElementRef<HTMLElement>;

  readonly vb = { w: 1200, h: 360 };
  disk = { cx: 150, cy: 195, r: 118 };

  items: HubItem[] = [
    {
      key: 'teal',
      tone: 'teal',
      index: 1,
      count: 0,
      title: 'N°de Clientes não impactados NF',
      subtitle: 'Processados e liberados para faturamento',
      icon: 'check-square',
      nav: { path: '/orders/list', queryParams: { order: 'invoiced' } },

      source: {
        kind: 'orders',
        req: { invoicedOrders: 'true' },
      },
    },
    {
      key: 'orange',
      tone: 'orange',
      index: 2,
      count: 0,
      title: 'N°de Clientes impactados',
      subtitle: 'Faturados sem NF-e enviada ao cliente',
      icon: 'file-text',
      nav: { path: '/orders/list', queryParams: { order: 'waiting' } },

      source: {
        kind: 'orders',
        req: { waitingOrders: 'true' },
      },
    },
    {
      key: 'pink',
      tone: 'pink',
      index: 3,
      count: 0,
      title: 'Pedidos com falha de pagamento',
      subtitle: 'Recusados, expirados ou inconsistentes',
      icon: 'alert-triangle',
      nav: { path: '/orders/list', queryParams: { order: 'incompleted' } },

      source: {
        kind: 'orders',
        req: { incompletedOrders: 'true' },
      },
    },
    {
      key: 'blue',
      tone: 'blue',
      index: 4,
      count: 0,
      title: 'Vendedores não impactados em loja',
      subtitle: 'Seller corrigido manualmente',
      icon: 'users',
      nav: { path: '/audit/events', queryParams: { type: 'seller' as OrderType } },

      source: {
        kind: 'audit',
        version: 'v2',
        filter: { actionNames: ['PutUpdateShopSellerVtexSync'] }
      },
    },
    {
      key: 'yellow',
      tone: 'yellow',
      index: 5,
      count: 0,
      title: 'Clientes atendidos pelo SAC',
      subtitle: 'Correções manuais de dados do cliente',
      icon: 'headphones',
      nav: { path: '/audit/events', queryParams: { type: 'customer' as OrderType } },

      source: {
        kind: 'audit',
        version: 'v2',
        filter: { actionNames: ['RectifyCustomer'] }
      },
    },
  ];

  activeKey: Tone = 'pink';

  hubDots: HubDot[] = [];
  paths: HubPath[] = [];

  private ro?: ResizeObserver;
  private raf = 0;

  constructor(private zone: NgZone, private router: Router,private ordersService: OrdersService,
  private auditService: AuditService) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.buildNow()));
      this.ro = new ResizeObserver(() => this.buildNow());
      this.ro.observe(this.stageRef.nativeElement);
      window.addEventListener('resize', this.buildNow, { passive: true });
    });

    this.loadCounts();
  }

  ngOnDestroy(): void {
    if (this.ro) this.ro.disconnect();
    window.removeEventListener('resize', this.buildNow as any);
    cancelAnimationFrame(this.raf);
  }

  toneClass(t: Tone) {
    return `is-${t}`;
  }

  formatCount(n: number) {
    return new Intl.NumberFormat('pt-BR').format(n);
  }

 private loadCounts() {
    const requests$: Observable<{ key: string; count: number }>[] = this.items.map((it) => {
      const src = it.source;
      if (!src) return of({ key: it.key, count: it.count });

      if (src.kind === 'orders') {
        const req = { page: 1, pageSize: 1, ...src.req };

        return this.ordersService.list(req).pipe(
          map((res: any) => ({
            key: it.key,
            count: Number(res?.pageInfo?.total ?? res?.page?.total ?? 0),
          })),
          catchError(() => of({ key: it.key, count: 0 }))
        );
      }

      if (src.kind === 'audit') {
        const filter = { page: 1, pageSize: 1, ...src.filter };

        return this.auditService.getEvents(filter as any, src.version ?? 'v1').pipe(
          map((res: any) => ({ key: it.key, count: Number(res?.page?.total ?? 0) })),
          catchError(() => of({ key: it.key, count: 0 }))
        );
      }

      return of({ key: it.key, count: it.count });
    });

    merge(...requests$)
      .pipe(
        tap(({ key, count }) => {
          this.items = this.items.map((it) =>
            it.key === key ? { ...it, count } : it
          );

          this.buildNow();
        }),
        finalize(() => {
          this.zone.runOutsideAngular(() => this.buildNow());
        })
      )
      .subscribe();
  }

  async handleClick(key: Tone) {
    const it = this.items.find((x) => x.key === key);
    if (!it) return;

    this.activeKey = key;

    await this.playTransition(it.tone);

    if (it.nav) {
      this.router.navigate([it.nav.path], { queryParams: it.nav.queryParams });
      return;
    }

    this.zone.runOutsideAngular(() => this.buildNow());
  }

  private buildNow = () => {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.buildFromDOM());
  };

  private buildFromDOM() {
    const stage = this.stageRef?.nativeElement;
    const svg = this.svgRef?.nativeElement;
    if (!stage || !svg) return;

    const pillEls = Array.from(stage.querySelectorAll<HTMLElement>('.oh-pill[data-key]'));

    const toSvg = (clientX: number, clientY: number) => {
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;

      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };

      const p = pt.matrixTransform(ctm.inverse());
      return { x: p.x, y: p.y };
    };

    const dots: HubDot[] = [];
    const paths: HubPath[] = [];

    const entryInsetPx = 16;
    const dotGap = 66;

    for (const it of this.items) {
      const el = pillEls.find(p => (p.dataset['key'] as Tone) === it.key);
      if (!el) continue;

      const r = el.getBoundingClientRect();
      const entryClientX = r.left + entryInsetPx;
      const centerClientY = r.top + r.height / 2;

      const entry = toSvg(entryClientX, centerClientY);
      const dot = { x: entry.x - dotGap, y: entry.y };

      dots.push({ key: it.key, tone: it.tone, x: dot.x, y: dot.y });

      const d = this.makeConnectedPath(
        this.disk.cx,
        this.disk.cy,
        this.disk.r,
        dot.x,
        dot.y,
        entry.x,
        entry.y
      );

      paths.push({ key: it.key, tone: it.tone, d });
    }

    this.zone.run(() => {
      this.hubDots = dots;
      this.paths = paths;
      queueMicrotask(() => feather.replace());
    });
  }

  private async playTransition(tone: Tone) {
    const root = this.rootRef?.nativeElement;
    const transition = this.transitionRef?.nativeElement;
    if (!root || !transition) return;

    root.style.setProperty('--hub-transition-tone', `var(--${tone})`);

    root.classList.remove('is-transitioning');
    void root.offsetWidth; // reflow
    root.classList.add('is-transitioning');

    await new Promise<void>((resolve) => {
      const fallback = window.setTimeout(resolve, 420);
      const onEnd = (ev: AnimationEvent) => {
        if (ev.target !== transition) return;
        window.clearTimeout(fallback);
        resolve();
      };
      transition.addEventListener('animationend', onEnd, { once: true });
    });

    root.classList.remove('is-transitioning');
  }

  private makeConnectedPath(
    dcx: number,
    dcy: number,
    dr: number,
    dotX: number,
    dotY: number,
    tx: number,
    ty: number
  ) {
    const vx = dotX - dcx;
    const vy = dotY - dcy;
    const len = Math.max(1, Math.hypot(vx, vy));

    const sx = dcx + (vx / len) * (dr * 0.92);
    const sy = dcy + (vy / len) * (dr * 0.18);

    const c1x = sx + 115;
    const c1y = sy + (dotY - sy) * 0.3;

    const c2x = dotX - 95;
    const c2y = dotY;

    const c3x = dotX + 95;
    const c3y = dotY;

    const c4x = tx - 110;
    const c4y = ty;

    return [
      `M ${sx.toFixed(2)} ${sy.toFixed(2)}`,
      `C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${dotX.toFixed(2)} ${dotY.toFixed(2)}`,
      `C ${c3x.toFixed(2)} ${c3y.toFixed(2)}, ${c4x.toFixed(2)} ${c4y.toFixed(2)}, ${tx.toFixed(2)} ${ty.toFixed(2)}`,
    ].join(' ');
  }
}
